import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Building2, User, Loader2, X, Sparkles, Check, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Smart Unified Data Logger Selector
 *
 * Props:
 *   value               {string} - Current data logger number/serial
 *   loggerSource        {'inventory' | 'customer'} - Source mode
 *   loggerItemId        {string} - Inventory item ID if from company inventory
 *   onChange            {function} - ({ data_logger_no, data_logger_source, data_logger_item_id, item }) => void
 *   inventoryItems      {Array} - Current docket inventory items array
 *   onInventoryItemsChange {function} - (newItems) => void
 *   disabled            {boolean} - Whether the field is disabled/locked
 *   excludeDocketId     {string} - Docket ID to exclude for in-use check in edit mode
 */
export default function DataLoggerSelector({
  value = '',
  loggerSource = 'inventory',
  loggerItemId = '',
  onChange,
  inventoryItems = [],
  onInventoryItemsChange,
  disabled = false,
  excludeDocketId,
}) {
  const [source, setSource] = useState(loggerSource || (value && !loggerItemId ? 'customer' : 'inventory'));
  const [availableLoggers, setAvailableLoggers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Sync internal source state if prop changes externally
  useEffect(() => {
    if (loggerSource) {
      setSource(loggerSource);
    } else if (value && !loggerItemId && !availableLoggers.some(l => l.sr_no === value)) {
      setSource('customer');
    }
  }, [loggerSource, value, loggerItemId, availableLoggers]);

  // Fetch available data loggers from company inventory
  const fetchAvailableLoggers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ item_type: 'Data Logger' });
      if (excludeDocketId) params.append('exclude_docket_id', excludeDocketId);
      const res = await fetch(`${API}/api/inventory/available?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
      });
      if (res.ok) {
        const data = await res.json();
        setAvailableLoggers(data || []);
      }
    } catch (err) {
      console.error('Failed to load available data loggers:', err);
    } finally {
      setLoading(false);
    }
  }, [excludeDocketId]);

  useEffect(() => {
    fetchAvailableLoggers();
  }, [fetchAvailableLoggers]);

  // Merge available loggers with any logger already present in inventoryItems (so edit mode shows it)
  const allLoggers = useMemo(() => {
    const map = new Map();
    availableLoggers.forEach(l => map.set(l.id, l));
    inventoryItems.filter(i => i.item_type === 'Data Logger').forEach(l => {
      if (!map.has(l.id)) map.set(l.id, l);
    });
    return Array.from(map.values());
  }, [availableLoggers, inventoryItems]);

  // Handle selection from Inventory dropdown
  const handleSelectInventoryLogger = (itemId) => {
    const selectedItem = allLoggers.find(l => l.id === itemId);
    if (!selectedItem) return;

    // 1. Notify parent of logger change
    if (onChange) {
      onChange({
        data_logger_no: selectedItem.sr_no,
        data_logger_source: 'inventory',
        data_logger_item_id: selectedItem.id,
        item: selectedItem,
      });
    }

    // 2. Auto-sync with inventoryItems (remove any previously auto-linked logger, add this one)
    if (onInventoryItemsChange) {
      const filtered = inventoryItems.filter(i =>
        !(i.auto_link_source === 'data_logger' || (i.item_type === 'Data Logger' && (i.id === loggerItemId || i.sr_no === value)))
      );
      const enrichedItem = {
        ...selectedItem,
        auto_link_source: 'data_logger',
        is_auto_linked: true,
      };
      onInventoryItemsChange([...filtered, enrichedItem]);
    }
  };

  // Handle free-text input for Customer's Own Logger
  const handleCustomerLoggerInput = (text) => {
    if (onChange) {
      onChange({
        data_logger_no: text,
        data_logger_source: 'customer',
        data_logger_item_id: null,
        item: null,
      });
    }

    // Remove any previously auto-linked inventory logger
    if (onInventoryItemsChange) {
      const filtered = inventoryItems.filter(i =>
        !(i.auto_link_source === 'data_logger' || (i.item_type === 'Data Logger' && i.id === loggerItemId))
      );
      if (filtered.length !== inventoryItems.length) {
        onInventoryItemsChange(filtered);
      }
    }
  };

  // Switch mode
  const handleSwitchSource = (newSource) => {
    setSource(newSource);
    if (newSource === 'customer') {
      // Remove any auto-linked inventory item
      if (onInventoryItemsChange && loggerItemId) {
        const filtered = inventoryItems.filter(i => i.id !== loggerItemId);
        onInventoryItemsChange(filtered);
      }
      if (onChange) {
        onChange({
          data_logger_no: '',
          data_logger_source: 'customer',
          data_logger_item_id: null,
          item: null,
        });
      }
    } else {
      if (onChange) {
        onChange({
          data_logger_no: '',
          data_logger_source: 'inventory',
          data_logger_item_id: null,
          item: null,
        });
      }
    }
  };

  const handleClear = () => {
    if (onChange) {
      onChange({
        data_logger_no: '',
        data_logger_source: source,
        data_logger_item_id: null,
        item: null,
      });
    }
    if (onInventoryItemsChange && loggerItemId) {
      onInventoryItemsChange(inventoryItems.filter(i => i.id !== loggerItemId));
    }
  };

  const selectedItemInInventory = allLoggers.find(
    l => l.id === loggerItemId || (value && l.sr_no === value)
  );

  return (
    <div className="space-y-1.5" data-testid="smart-data-logger-selector">
      {/* Mode Switcher Pill */}
      <div className="flex items-center justify-between gap-2">
        <div className="inline-flex p-0.5 bg-gray-100 rounded-md border border-gray-200 text-[11px] font-ibm">
          <button
            type="button"
            disabled={disabled}
            onClick={() => handleSwitchSource('inventory')}
            className={`flex items-center gap-1 px-2 py-0.5 rounded transition-all ${
              source === 'inventory'
                ? 'bg-white text-emerald-700 font-semibold shadow-xs'
                : 'text-text-secondary hover:text-text-dark'
            }`}
          >
            <Building2 className="w-3 h-3 text-emerald-600" />
            Company Inventory
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={() => handleSwitchSource('customer')}
            className={`flex items-center gap-1 px-2 py-0.5 rounded transition-all ${
              source === 'customer'
                ? 'bg-white text-blue-700 font-semibold shadow-xs'
                : 'text-text-secondary hover:text-text-dark'
            }`}
          >
            <User className="w-3 h-3 text-blue-600" />
            Customer's Own
          </button>
        </div>

        {value && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="text-[11px] text-gray-400 hover:text-red-500 flex items-center gap-0.5 font-ibm transition-colors"
            title="Clear data logger"
          >
            <X className="w-3 h-3" /> Clear
          </button>
        )}
      </div>

      {/* Input / Dropdown Area */}
      {source === 'inventory' ? (
        <div className="relative">
          <Select
            value={selectedItemInInventory?.id || ''}
            onValueChange={handleSelectInventoryLogger}
            disabled={disabled || loading}
          >
            <SelectTrigger
              data-testid="data-logger-select"
              className="font-ibm text-xs h-9 bg-white border-gray-200 focus:ring-1 focus:ring-emerald-500"
            >
              <SelectValue placeholder={loading ? 'Loading loggers...' : 'Select available logger...'} />
            </SelectTrigger>
            <SelectContent className="max-h-60">
              {allLoggers.length === 0 ? (
                <div className="p-3 text-center text-xs text-text-secondary font-ibm">
                  <AlertCircle className="w-4 h-4 mx-auto mb-1 text-amber-500" />
                  No Data Loggers available in stock.
                  <button
                    type="button"
                    onClick={() => handleSwitchSource('customer')}
                    className="block mx-auto mt-1.5 text-blue-600 hover:underline font-semibold"
                  >
                    Use Customer's Logger
                  </button>
                </div>
              ) : (
                allLoggers.map(l => {
                  const details = [
                    l.sr_no,
                    l.temp_range,
                    l.manufacture,
                  ].filter(Boolean).join(' · ');

                  return (
                    <SelectItem key={l.id} value={l.id} className="text-xs font-ibm">
                      <div className="flex items-center justify-between gap-3 w-full">
                        <span className="font-semibold text-text-dark">{l.sr_no}</span>
                        {(l.temp_range || l.manufacture) && (
                          <span className="text-[11px] text-text-secondary">
                            ({[l.temp_range, l.manufacture].filter(Boolean).join(' / ')})
                          </span>
                        )}
                      </div>
                    </SelectItem>
                  );
                })
              )}
            </SelectContent>
          </Select>

          {selectedItemInInventory && (
            <div className="flex items-center gap-1.5 mt-1 text-[11px] text-emerald-700 font-ibm">
              <Check className="w-3 h-3 text-emerald-600 flex-shrink-0" />
              <span>
                Auto-linked to docket inventory (<strong className="font-semibold">{selectedItemInInventory.sr_no}</strong>)
              </span>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-1">
          <Input
            data-testid="data-logger-customer-input"
            type="text"
            placeholder="Type customer's logger sr no. (e.g. SENSITECH-8821)"
            value={value || ''}
            onChange={e => handleCustomerLoggerInput(e.target.value)}
            disabled={disabled}
            className="font-ibm text-xs h-9 bg-white border-gray-200 focus:ring-1 focus:ring-blue-500"
          />
          <p className="text-[10px] text-text-secondary font-ibm">
            Customer-supplied device. Printed on docket; does not deduct company inventory.
          </p>
        </div>
      )}
    </div>
  );
}
